import { sessionFreshen } from '@/api/session/sessionFreshen'
import { useApi } from '@/hooks/useApi'
import { BasicPage } from '@/components/layout/BasicPage'
import { consumeLoginRedirectTarget } from '@/utilities/authRedirect'
import { routes } from '@/utilities/routes'
import { authGoogleVerifySpec } from 'aevocam-rest-api-spec'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

export function GoogleAuthVerifyPage() {

  const [searchParams] = useSearchParams()
  const api = useApi()
  const navigate = useNavigate()

  const [status, setStatus] = useState<'verifying' | 'ok' | 'error'>('verifying')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [tenantId, setTenantId] = useState<string>()

  const verifyAttemptedRef = useRef(false)

  //--------------------------------------------------------------------------------
  useEffect(() => {
    const code = searchParams.get('code')
    const state = searchParams.get('state')
    let tenantId: string | undefined

    if (state) {
      const jsonText = decodeBase64urlToUtf8(state)
      const json = JSON.parse(jsonText)
      tenantId = json.tenantId
      setTenantId(tenantId)
    }

    if (!code || !state) {
      setStatus('error')
      setErrorMessage('Missing code or state from Google.')
      return
    }

    // Prevent double run in React Strict Mode (dev). The auth 
    // code is one-time use only.
    if (verifyAttemptedRef.current) return
    verifyAttemptedRef.current = true

    async function fn() {
      const res = await api.request(authGoogleVerifySpec, {
        body: { code, state },
      })
      if (res.status !== 200 || !res.data) {
        setStatus('error')
        setErrorMessage(res.error ?? 'Verification failed.')
        return
      }

      console.log('Setting sessionId', res.data.sessionId)
      localStorage.setItem('sessionId', res.data.sessionId)

      await sessionFreshen(api, res.data.tenantId)

      setStatus('ok')
      console.log('Done')
      navigate(
        consumeLoginRedirectTarget() ?? routes.home(res.data.tenantId),
        { replace: true }
      )
    }

    fn()
  }, [searchParams, api])

  //--------------------------------------------------------------------------------
  // Render error
  if (status === 'error') {
    return (
      <BasicPage title="Google sign-in">
        <p>{errorMessage}</p>
        <p>
          <Link to={routes.logIn(tenantId)}>Return to log in</Link>
        </p>
      </BasicPage>
    )
  }

  //--------------------------------------------------------------------------------
  // Render
  return (
    <BasicPage title="Google sign-in">
      <p>Completing sign-in…</p>
    </BasicPage>
  )
}


//--------------------------------------------------------------------------------
/** Decode base64url to UTF-8 string (browser-safe, no Node Buffer). */
function decodeBase64urlToUtf8(base64url: string): string {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = base64.length % 4
  if (pad) base64 += '='.repeat(4 - pad)
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new TextDecoder().decode(bytes)
}
